import { useState, type FormEvent } from 'react';
import { Link } from '@tanstack/react-router';
import {
  useAddTrustedNetwork, useFraudAlerts, useFraudHolds, useFraudRules, useReleaseHold, useRemoveTrustedNetwork,
  useReviewAlert, useTrustedNetworks,
  type FraudAlert, type FraudHold,
} from '../../lib/api/hooks-fraud.js';
import { useTenants } from '../../lib/api/hooks.js';
import { useSession } from '../../lib/auth/session.js';
import { useStepUp, StepUpPrompt, type StepUp } from '../../lib/auth/stepup.js';
import { relativeTime } from '../../lib/format.js';
import { Badge, Empty, ErrorBanner, SectionHead, Segmented, TableSkeleton } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { RowAction } from '../../components/ui/RowAction.js';
import { confirmAction } from '../../components/ui/Confirm.js';

/**
 * Toll fraud (docs/39): what the rules held, what they noticed, and releasing a hold.
 *
 * Everybody with fraud:read sees what is in their scope -- the account its own, the MSP its
 * accounts', the platform all. Releasing and reviewing is fraud:write (MSP and platform, step-up):
 * the client is told and asks, the provider decides (docs/16 Layer 4).
 */
export function FraudPage() {
  const can = useSession((s) => s.can);
  const isAccount = useSession((s) => s.session?.scope.level === 'tenant' || s.session?.scope.level === 'site');
  const [open, setOpen] = useState<'open' | 'all'>('open');
  const holds = useFraudHolds({});
  const alerts = useFraudAlerts({ open: open === 'open' ? true : undefined });
  const rules = useFraudRules();
  const step = useStepUp();
  const writer = can('fraud:write');

  return (
    <>
      <div className="arc-page-head">
        <div>
          <h1 className="arc-h1">Fraud</h1>
          <p className="arc-muted" style={{ marginTop: 3 }}>
            Held extensions cannot make chargeable calls. Internal and emergency calls still work.
          </p>
        </div>
      </div>

      <StepUpPrompt step={step} what="Releasing a hold or reviewing an alert">
        A released extension can spend money again. Confirm with your password.
      </StepUpPrompt>

      <SectionHead level="section" title="Held now" />
      <div className="arc-card" style={{ marginBottom: 18 }}>
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead><tr>{!isAccount && <th>Account</th>}<th>Extension</th><th>Why</th><th>Held</th><th /></tr></thead>
            {holds.data === undefined ? <TableSkeleton cols={isAccount ? 4 : 5} /> : (
              <tbody>
                {holds.data.data.map((h) => <HoldRow key={h.id} h={h} isAccount={isAccount} writer={writer} step={step} />)}
              </tbody>
            )}
          </table>
        </div>
        {holds.data && holds.data.data.length === 0 && <Empty icon="check" title="Nothing held" />}
        {holds.error && <ErrorBanner error={holds.error} />}
      </div>

      <SectionHead level="section" title="Alerts" action={
        <Segmented label="Which alerts" value={open} onChange={setOpen}
          options={[['open', 'Not reviewed'], ['all', 'All']] as const} />} />
      <div className="arc-card" style={{ marginBottom: 18 }}>
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead><tr><th>When</th>{!isAccount && <th>Account</th>}<th>Extension</th><th>Rule</th><th>What it saw</th><th /></tr></thead>
            {alerts.data === undefined ? <TableSkeleton cols={isAccount ? 5 : 6} /> : (
              <tbody>
                {alerts.data.data.map((a) => <AlertRow key={a.id} a={a} isAccount={isAccount} writer={writer} step={step} />)}
              </tbody>
            )}
          </table>
        </div>
        {alerts.data && alerts.data.data.length === 0 && (
          <Empty icon="check" title={open === 'open' ? 'Nothing to review' : 'No alerts yet'} />
        )}
        {alerts.error && <ErrorBanner error={alerts.error} />}
      </div>

      <TrustedNetworks isAccount={isAccount} writer={writer} step={step} />

      <SectionHead level="section" title="Rules" />
      <div className="arc-card">
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead><tr><th>Rule</th><th>Trips when</th><th>Then</th></tr></thead>
            <tbody>
              {(rules.data?.data ?? []).map((r) => (
                <tr key={r.signal}>
                  <td style={{ fontWeight: 550 }}>{r.label}</td>
                  <td className="arc-muted">{r.describe}</td>
                  <td>{r.action === 'hold'
                    ? (r.account ? 'Hold the account' : 'Hold the extension')
                    : 'Alert'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

const accountCell = (tenantSlug: string, tenantName: string) => (
  <td><Link to="/accounts/$accountId" params={{ accountId: tenantSlug }}>{tenantName}</Link></td>
);

function HoldRow({ h, isAccount, writer, step }: { h: FraudHold; isAccount: boolean; writer: boolean; step: StepUp }) {
  const release = useReleaseHold();
  return (
    <>
      <tr>
        {!isAccount && accountCell(h.tenant_slug, h.tenant_name)}
        <td className={h.extension_number ? 'arc-mono' : undefined}>{h.extension_number ?? 'Whole account'}</td>
        <td>{h.reason}{h.placed_by && <div className="arc-subtle" style={{ fontSize: 11.5 }}>by {h.placed_by}</div>}</td>
        <td className="arc-num" title={new Date(h.placed_at).toLocaleString('en-ZA')}>{relativeTime(h.placed_at)}</td>
        <td className="arc-td-right">
          {writer && (
            <NoteAction label="Release" confirm="Release" placeholder="Why it is safe: password changed, calls were theirs…"
              busy={release.isPending}
              onSubmit={(note) => step.run(() => release.mutateAsync({ id: h.id, note }))} />
          )}
        </td>
      </tr>
      {release.error && <tr><td colSpan={5}><ErrorBanner error={release.error} /></td></tr>}
    </>
  );
}

function AlertRow({ a, isAccount, writer, step }: { a: FraudAlert; isAccount: boolean; writer: boolean; step: StepUp }) {
  const review = useReviewAlert();
  return (
    <>
      <tr>
        <td className="arc-num" title={new Date(a.detected_at).toLocaleString('en-ZA')}>{relativeTime(a.detected_at)}</td>
        {!isAccount && accountCell(a.tenant_slug, a.tenant_name)}
        <td className="arc-mono">{a.extension_number ?? 'Account'}</td>
        <td>
          <div style={{ fontWeight: 550 }}>{a.rule}</div>
          {a.action === 'hold'
            ? <Badge tone={a.hold_released_at ? 'muted' : 'danger'}>{a.hold_released_at ? 'Held, released' : 'Held'}</Badge>
            : <Badge tone="warn">alert</Badge>}
        </td>
        <td style={{ fontSize: 12.5 }}>
          {a.lines.length === 0 ? <span className="arc-muted">{a.describe}</span> : a.lines.map(([k, v]) => (
            <div key={k}><span className="arc-subtle">{k}:</span> {v}</div>
          ))}
          {a.reviewed_at && <div className="arc-subtle" style={{ marginTop: 4 }}>Reviewed{a.reviewed_by ? ` by ${a.reviewed_by}` : ''}: {a.review_note}</div>}
        </td>
        <td className="arc-td-right">
          {writer && !a.reviewed_at && (
            <NoteAction label="Mark reviewed" confirm="Save" placeholder="What it was"
              busy={review.isPending}
              onSubmit={(note) => step.run(() => review.mutateAsync({ id: a.id, note }))} />
          )}
        </td>
      </tr>
      {review.error && <tr><td colSpan={6}><ErrorBanner error={review.error} /></td></tr>}
    </>
  );
}

/** A button that asks for a note before it acts: a release or a review is explained, always. */
function NoteAction({ label, confirm, placeholder, busy, onSubmit }: {
  label: string; confirm: string; placeholder: string; busy: boolean;
  onSubmit: (note: string) => Promise<unknown>;
}) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  if (!open) {
    return <button type="button" className="arc-btn arc-btn-sm" onClick={() => setOpen(true)}>{label}</button>;
  }
  return (
    <form className="arc-row" style={{ gap: 6, justifyContent: 'flex-end' }} onSubmit={(e: FormEvent) => {
      e.preventDefault();
      void onSubmit(note.trim()).then((r) => { if (r !== undefined) { setOpen(false); setNote(''); } });
    }}>
      <input className="arc-input" style={{ minWidth: 220 }} autoFocus value={note} maxLength={1000}
        placeholder={placeholder} aria-label="Note" onChange={(e) => setNote(e.target.value)} />
      <button type="submit" className="arc-btn arc-btn-primary arc-btn-sm" disabled={busy || note.trim().length < 3}>{confirm}</button>
      <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" aria-label="Cancel" onClick={() => setOpen(false)}>
        <Icon name="close" size={13} />
      </button>
    </form>
  );
}

/**
 * Networks an account's phones may register from without the location rules judging them: its
 * VPN, a hosted softphone service, an office abroad (docs/39). Moves inside the account's own
 * country are never judged anyway, so this is only for addresses abroad.
 */
function TrustedNetworks({ isAccount, writer, step }: { isAccount: boolean; writer: boolean; step: StepUp }) {
  const nets = useTrustedNetworks();
  const add = useAddTrustedNetwork();
  const remove = useRemoveTrustedNetwork();
  const tenants = useTenants({ limit: 500 });
  const [tenantId, setTenantId] = useState('');
  const [network, setNetwork] = useState('');
  const [note, setNote] = useState('');
  const list = nets.data?.data ?? [];

  return (
    <>
      <SectionHead level="section" title="Trusted networks"
        sub="Phones registering from these addresses are not checked for impossible travel or new countries. Moves inside the account's own country are never checked." />
      <div className="arc-card" style={{ marginBottom: 18 }}>
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead><tr>{!isAccount && <th>Account</th>}<th>Network</th><th>Why</th><th>Added</th><th /></tr></thead>
            <tbody>
              {list.map((n) => (
                <tr key={n.id}>
                  {!isAccount && <td>{n.tenant_name}</td>}
                  <td className="arc-mono">{n.network}</td>
                  <td>{n.note}</td>
                  <td className="arc-num">{relativeTime(n.created_at)}{n.created_by && <div className="arc-subtle" style={{ fontSize: 11.5 }}>{n.created_by}</div>}</td>
                  <td className="arc-td-right">
                    {writer && (
                      <RowAction kind="delete" label={`Remove ${n.network}`} disabled={remove.isPending}
                        onClick={() => { void confirmAction({ title: `Stop trusting ${n.network}?`, action: 'Stop trusting', danger: true }).then((ok) => { if (ok) void step.run(() => remove.mutateAsync(n.id)); }); }} />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {nets.data && list.length === 0 && <Empty icon="globe" title="None trusted" />}
        {writer && (
          <form className="arc-card-body arc-row" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', borderTop: '1px solid var(--arc-border)' }}
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              void step.run(() => add.mutateAsync({ tenantId, network: network.trim(), note: note.trim() }))
                .then((r) => { if (r !== undefined) { setNetwork(''); setNote(''); } });
            }}>
            <div className="arc-field" style={{ margin: 0, minWidth: 200 }}>
              <label className="arc-label" htmlFor="tn-acct">Account</label>
              <select id="tn-acct" className="arc-select" value={tenantId} onChange={(e) => setTenantId(e.target.value)} required>
                <option value="">Choose…</option>
                {(tenants.data?.data ?? []).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div className="arc-field" style={{ margin: 0, minWidth: 180 }}>
              <label className="arc-label" htmlFor="tn-net">Address or network</label>
              <input id="tn-net" className="arc-input arc-mono" value={network} placeholder="196.25.1.0/24"
                onChange={(e) => setNetwork(e.target.value)} required />
            </div>
            <div className="arc-field" style={{ margin: 0, flex: 1, minWidth: 200 }}>
              <label className="arc-label" htmlFor="tn-note">Why</label>
              <input id="tn-note" className="arc-input" value={note} maxLength={200} placeholder="Their VPN in Frankfurt"
                onChange={(e) => setNote(e.target.value)} required />
            </div>
            <button type="submit" className="arc-btn arc-btn-primary arc-btn-sm"
              disabled={add.isPending || !tenantId || !network.trim() || note.trim().length < 3}>Trust</button>
          </form>
        )}
        {(add.error || remove.error) && <ErrorBanner error={add.error ?? remove.error} />}
      </div>
    </>
  );
}
